<template>
  <div class="root">
    <div v-if="saved" class="sf-alert sf-align-c sf-alert-success">
      {{ I18n.t('account_rounding_configuration.update_message') }}
    </div>
    <div class="sf-pageheader sf-below-4 sf-border-box">
      <div class="sf-grid">
        <div class="sf-row sf-row-center">
          <div class="sf-col sf-xs-12 sf-sm-7">
            <div
              class="
                sf-level
                sf-level-between
                sf-level-m
                sf-level-ignore-mobile
                sf-pad-h-3
              "
            >
              <div class="sf-level-item">
                <div class="sf-pageheader-title">
                  {{ I18n.t('account_rounding_configuration.title') }}
                </div>
              </div>
              <div class="sf-level-item">
                <div class="sf-level">
                  <a class="sf-btn sf-btn-success" @click="handleSubmit">{{
                    I18n.t('shared.save')
                  }}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="sf-grid">
      <div class="sf-row sf-row-center">
        <div class="sf-col sf-xs-12 sf-sm-7">
          <div class="sf-below-3 sf-pad-h-3">
            <div class="sf-title sf-font-l sf-below-1">
              {{ I18n.t('account_rounding_configuration.subtitle') }}
            </div>
            <div
              v-dompurify-html="
                I18n.t('account_rounding_configuration.subtitle_hint', {
                  period: ledgerDate,
                })
              "
            ></div>
          </div>
          <div class="sf-layer-white sf-pad-2">
            <div
              v-if="!roundingPresent"
              class="sf-alert sf-alert-warning sf-pad-h-2 sf-below-3"
            >
              {{ I18n.t('account_rounding_configuration.warning_message') }}
            </div>
            <div class="sf-below-3">
              <div class="sf-pad-h-2 sf-below-2">
                <div class="sf-title sf-fwdb sf-font-m">
                  {{
                    I18n.t('account_rounding_configuration.pl_rounding_account')
                  }}
                </div>
                <div class="sf-level sf-level-small">
                  <div class="sf-level-item">
                    {{
                      I18n.t(
                        'account_rounding_configuration.rounding_difference',
                        {
                          period: ledgerDate,
                          value: roundingDifferencePL.ones,
                        }
                      )
                    }}
                  </div>
                  <span
                    :title="
                      I18n.t(
                        'account_rounding_configuration.rounding_difference_tooltip',
                        {
                          thousands: roundingDifferencePL.thousands,
                          millions: roundingDifferencePL.millions,
                        }
                      )
                    "
                    class="sf-level-item sf-c-text-light icon icon-info-circle"
                    data-placement="bottom"
                    data-toggle="tooltip"
                  ></span>
                </div>
              </div>
              <div
                v-if="plDisabledMessage"
                class="sf-bg-text-lighter sf-layer-rounded sf-pad-2 sf-below-2"
              >
                {{ plDisabledMessage }}
              </div>
              <select
                v-else
                ref="profitAndLossAccountSelect"
                :data-ledger-id="ledgerId"
                :data-selected-label="roundingPLAccount.number"
                :data-selected-text="roundingPLAccount.name"
                :data-selected-value="roundingPLAccount.value"
                data-behavior="ledger-account-select2"
                data-p-and-l="1"
              ></select>
              <div v-if="plError" class="sf-pad-h-2 sf-pad-t-1 sf-c-danger">
                {{ plError }}
              </div>
            </div>
            <div class="sf-below-2">
              <div class="sf-pad-h-2 sf-below-2">
                <div class="sf-title sf-fwdb sf-font-m">
                  {{
                    I18n.t(
                      'account_rounding_configuration.balance_sheet_rounding_account'
                    )
                  }}
                </div>
                <div class="sf-level sf-level-small">
                  <div class="sf-level-item">
                    {{
                      I18n.t(
                        'account_rounding_configuration.rounding_difference',
                        {
                          period: ledgerDate,
                          value: roundingDifferenceBS.ones,
                        }
                      )
                    }}
                  </div>
                  <span
                    :title="
                      I18n.t(
                        'account_rounding_configuration.rounding_difference_tooltip',
                        {
                          thousands: roundingDifferenceBS.thousands,
                          millions: roundingDifferenceBS.millions,
                        }
                      )
                    "
                    class="sf-level-item sf-c-text-light icon icon-info-circle"
                    data-placement="bottom"
                    data-toggle="tooltip"
                  ></span>
                </div>
              </div>
              <div
                v-if="bsDisabledMessage"
                class="sf-bg-text-lighter sf-layer-rounded sf-pad-2 sf-below-2"
              >
                {{ bsDisabledMessage }}
              </div>
              <select
                v-else
                ref="balanceSheetAccountSelect"
                :data-ledger-id="ledgerId"
                :data-selected-label="roundingBSAccount.number"
                :data-selected-text="roundingBSAccount.name"
                :data-selected-value="roundingBSAccount.value"
                data-balance-sheet="1"
                data-behavior="ledger-account-select2"
              ></select>
              <div v-if="bsError" class="sf-pad-h-2 sf-pad-t-1 sf-c-danger">
                {{ bsError }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SFetch from '@app/utils/sfetch'

export default {
  name: 'RoundingConfiguration',
  props: {
    ledgerId: {
      type: Number,
      required: true,
    },
    ledgerDate: {
      type: String,
      required: true,
    },
    roundingDifferencePL: {
      type: Object,
      required: true,
    },
    roundingDifferenceBS: {
      type: Object,
      required: true,
    },
    roundingPLAccount: {
      type: Object,
      default: () => {},
    },
    roundingBSAccount: {
      type: Object,
      default: () => {},
    },
    existingAccounts: {
      type: Object,
      required: true,
    },
  },

  data: function () {
    return {
      saved: false,
      plError: this.roundingPLAccount.error,
      bsError: this.roundingBSAccount.error,
      bsAccount: this.roundingBSAccount.number,
      plAccount: this.roundingPLAccount.number,
    }
  },

  computed: {
    roundingPresent() {
      // Account was set OR no account found in the company
      return (
        (!!this.plAccount || !this.existingAccounts.pl) &&
        (!!this.bsAccount || !this.existingAccounts.bs)
      )
    },
    bsDisabledMessage() {
      if (!this.existingAccounts.bs) {
        return I18n.t('account_rounding_configuration.disabled_message', {
          rounding: I18n.t(
            'account_rounding_configuration.balance_sheet_rounding'
          ),
          accounts: I18n.t(
            'account_rounding_configuration.balance_sheet_accounts'
          ),
        })
      }

      return false
    },
    plDisabledMessage() {
      if (!this.existingAccounts.pl) {
        return I18n.t('account_rounding_configuration.disabled_message', {
          rounding: I18n.t('account_rounding_configuration.pl_rounding'),
          accounts: I18n.t('account_rounding_configuration.pl_accounts'),
        })
      }

      return false
    },
  },

  methods: {
    handleSubmit() {
      this.resetErrors()

      const selectedProfitAndLossAccount =
        this.$refs.profitAndLossAccountSelect &&
        this.$refs.profitAndLossAccountSelect.value
      const selectedBalanceSheetAccount =
        this.$refs.balanceSheetAccountSelect &&
        this.$refs.balanceSheetAccountSelect.value
      const path = Silverfin.Routes.accounts_roundings_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId
      )

      SFetch.json(path, {
        method: 'PATCH',
        body: {
          accounts_rounding: {
            pl_account: selectedProfitAndLossAccount,
            bs_account: selectedBalanceSheetAccount,
          },
        },
      })
        .then(() => {
          this.saved = true
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const component = this
          component.bsAccount = selectedBalanceSheetAccount
          component.plAccount = selectedProfitAndLossAccount
          window.setTimeout(() => {
            component.saved = false
          }, 3 * 1000)
        })
        .catch((error) => {
          // We don't want to show alert for Browser error like cancelled requests
          if (error.status !== 0) {
            this.saved = false
            if (error.body.errors) {
              this.showErrors(error.body.errors)
            } else {
              alert(`Something went wrong: Status: ${error.status}`)
            }
          }
        })
    },

    resetErrors() {
      this.plError = null
      this.bsError = null
    },

    showErrors(errors) {
      const generalErrors = []

      errors.forEach((errorMessage) => {
        if (typeof errorMessage === 'object' && errorMessage !== null) {
          if (errorMessage['pAndL']) {
            this.plError = errorMessage['pAndL']
          } else if (errorMessage['balanceSheet']) {
            this.bsError = errorMessage['balanceSheet']
          }
        } else {
          generalErrors.push(errorMessage)
        }
      })

      if (generalErrors.length > 0) {
        alert(`Something went wrong: ${generalErrors.join()}`)
      }
    },
  },
}
</script>
